import type { Hold, RouteLine } from '../../../types/climb';
import { circularHold, clampFraction, holdRadius } from '../photo-hold-roles';
import { holdOutline, mapOutline, outlinedHold, type OutlinePoint } from '../hold-outline';
import type { ZoomPan } from '../board-grid/useZoomPan';

/** A part of a photo, in fractions of it. */
export interface CropRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The whole picture — what "use it as it is" means. */
export const WHOLE_PHOTO: CropRect = { left: 0, top: 0, width: 1, height: 1 };

/**
 * What part of the photo is on screen right now.
 *
 * Read by asking the zoom where the frame's two corners land on the content,
 * rather than by doing its arithmetic again out here: the transform is the
 * zoom's business, and a second copy of it would be one to keep in step.
 */
export function visibleRect(zoom: ZoomPan): CropRect {
  const frame = zoom.frameRef.current?.getBoundingClientRect();
  if (!frame) return WHOLE_PHOTO;

  const topLeft = zoom.toContent(frame.left, frame.top);
  const bottomRight = zoom.toContent(frame.right, frame.bottom);
  if (!topLeft || !bottomRight) return WHOLE_PHOTO;

  const left = clampFraction(topLeft.x);
  const top = clampFraction(topLeft.y);

  return {
    left,
    top,
    width: Math.max(clampFraction(bottomRight.x) - left, 0.01),
    height: Math.max(clampFraction(bottomRight.y) - top, 0.01),
  };
}

/** Whether this rect is worth cutting at all. */
export function isCrop(rect: CropRect): boolean {
  return rect.width < 0.999 || rect.height < 0.999;
}

/**
 * Cut the photo down to that rect.
 *
 * A plain canvas, at the source's own resolution — cropping is meant to throw
 * away the wall around the climb, not to throw away detail in it.
 */
export async function cropImage(
  url: string,
  rect: CropRect,
  naturalWidth: number,
  naturalHeight: number
): Promise<{ file: File; width: number; height: number }> {
  const image = await loadImage(url);
  const width = Math.max(Math.round(rect.width * naturalWidth), 1);
  const height = Math.max(Math.round(rect.height * naturalHeight), 1);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('The photo could not be cropped');

  context.drawImage(
    image,
    rect.left * naturalWidth,
    rect.top * naturalHeight,
    rect.width * naturalWidth,
    rect.height * naturalHeight,
    0,
    0,
    width,
    height
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92)
  );
  if (!blob) throw new Error('The photo could not be cropped');

  return { file: new File([blob], 'climb.jpg', { type: 'image/jpeg' }), width, height };
}

/**
 * The same cut, on a picture the server holds rather than one in hand.
 *
 * Fetched into a blob first: a canvas that has drawn an image from another
 * origin is tainted and refuses to give its pixels back, and the panel's photos
 * are served from the API host while the panel itself is not. The copy is let go
 * of straight away — it is the whole photograph, held only for the cut.
 */
export async function cropStoredImage(
  url: string,
  rect: CropRect,
  naturalWidth: number,
  naturalHeight: number
): Promise<{ file: File; width: number; height: number }> {
  const blob = await fetch(url).then((response) => response.blob());
  const objectUrl = URL.createObjectURL(blob);

  try {
    return await cropImage(objectUrl, rect, naturalWidth, naturalHeight);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * The marks, moved into the crop.
 *
 * Every one is a fraction of the picture, so cutting the picture changes all of
 * them. A mark whose middle fell outside the cut is **dropped**: it is not on
 * the photo any more, and a hold drawn off the edge of the wall is not a hold.
 *
 * A circle is rebuilt through `circularHold` against the cropped size rather
 * than having its stored numbers scaled: its width is a fraction of the photo's
 * width and its height a fraction of its *height*, so a crop that changes the
 * proportions would turn every circle into an ellipse if the two were scaled as
 * one.
 *
 * A hold that knows its own shape has **every point** put through the same cut
 * and keeps the shape; its rectangle is then derived from the moved points, as
 * it is everywhere else. Scaling only the rectangle would leave the outline
 * describing the photo it was measured on and the box describing the cut — two
 * answers about one hold, which is the thing this geometry exists to prevent.
 */
export function cropHolds(
  holds: Hold[],
  rect: CropRect,
  croppedWidth: number,
  croppedHeight: number
): Hold[] {
  const intoCrop = (point: OutlinePoint): OutlinePoint => [
    clampFraction((point[0] - rect.left) / rect.width),
    clampFraction((point[1] - rect.top) / rect.height),
  ];

  return holds
    .filter(
      (hold) =>
        hold.x >= rect.left &&
        hold.x <= rect.left + rect.width &&
        hold.y >= rect.top &&
        hold.y <= rect.top + rect.height
    )
    // Moved with the framing, not redrawn: the swap a hold's hand may carry
    // survives the cut with it.
    .map((hold) => {
      const outline = holdOutline(hold);
      if (outline) {
        return { ...hold, ...outlinedHold(mapOutline(outline, intoCrop), hold.color) };
      }

      return {
        ...hold,
        ...circularHold(
          (hold.x - rect.left) / rect.width,
          (hold.y - rect.top) / rect.height,
          // The radius is a fraction of the height; the height just shrank.
          holdRadius(hold) / rect.height,
          hold.color,
          croppedWidth,
          croppedHeight
        ),
      };
    });
}

/**
 * The line, moved into the crop.
 *
 * Points are clamped rather than dropped: a line is a path, and losing the
 * middle of it would join two points that were never next to each other. A route
 * that runs off the cut is drawn to the edge and stops there, which is what it
 * looks like on the cropped photo anyway.
 *
 * The corridor and the thickness are fractions of the picture's **long edge**,
 * so they follow how that edge changed rather than the crop's width or height.
 */
export function cropLine(
  line: RouteLine | null,
  rect: CropRect,
  naturalWidth: number,
  naturalHeight: number,
  croppedWidth: number,
  croppedHeight: number
): RouteLine | null {
  if (!line) return null;

  const longEdgeBefore = Math.max(naturalWidth, naturalHeight);
  const longEdgeAfter = Math.max(croppedWidth, croppedHeight);
  const scale = longEdgeBefore / longEdgeAfter;

  return {
    ...line,
    points: line.points.map((point) => ({
      x: clampFraction((point.x - rect.left) / rect.width),
      y: clampFraction((point.y - rect.top) / rect.height),
    })),
    corridor: line.corridor * scale,
    ...(line.thickness === undefined ? {} : { thickness: line.thickness * scale }),
  };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The photo could not be read'));
    image.src = url;
  });
}
